import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer } from 'react';
// third-party
import { Chance } from 'chance';
import jwtDecode from 'jwt-decode';

// reducer - state management
import { LOGIN, LOGOUT } from '@/from-theme/contexts/auth-reducer/actions';
import authReducer from '@/from-theme/contexts/auth-reducer/auth';

// project import
import Loader from '@/from-theme/components/Loader';
import axios from '@/from-theme/utils//axios';
import { getResponseInterceptor } from '@/store/api/helpers/helpers';
import { apiBaseUrl } from '@/store/api/constants/baseURL';

const chance = new Chance();

// constant
const initialState = {
  isLoggedIn: false,
  isInitialized: false,
  user: null,
  userData: null
};

// eslint-disable-next-line no-unused-vars
const verifyToken = (serviceToken) => {
  if (!serviceToken) {
    return false;
  }
  try {
    const decoded = jwtDecode(serviceToken);
    if (!decoded || !decoded.exp) {
      return true;
    }
    return decoded.exp > Date.now() / 1000;
  } catch (e) {
    console.error('Failed to verify token:', e);
    return false;
  }
};

const setSession = (serviceToken) => {
  if (serviceToken) {
    localStorage.setItem('serviceToken', serviceToken);
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
  } else {
    localStorage.removeItem('serviceToken');
    delete axios.defaults.headers.common.Authorization;
  }
};

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext(null);

export const JWTProvider = ({ children }) => {


  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const init = async () => {
      try {
        const serviceToken = window.localStorage.getItem('serviceToken');
        const storedUserData = window.localStorage.getItem('userData');
        let parsedUserData = null;
        if (storedUserData) {
          try {
            parsedUserData = JSON.parse(storedUserData);
          } catch (e) {
            console.error('Failed to parse cached userData', e);
          }
        }

        if (serviceToken) {
          setSession(serviceToken);

          try {
            const responseOriginal = await axios.get(apiBaseUrl + "authenticate");
            const response = getResponseInterceptor(responseOriginal);
            const { data = {}, serviceToken: newServiceToken = "", entitlement = {} } = response?.data?.response || {};

            if (response?.status === 200 && response?.data?.status === true) {
              const activeToken = newServiceToken || serviceToken;
              setSession(activeToken);
              const userPayload = {
                data: Object.keys(data).length > 0 ? data : (parsedUserData?.data || {}),
                serviceToken: activeToken,
                entitlement: Object.keys(entitlement).length > 0 ? entitlement : (parsedUserData?.entitlement || {})
              };
              window.localStorage.setItem('userData', JSON.stringify(userPayload));
              dispatch({
                type: LOGIN,
                payload: {
                  isLoggedIn: true,
                  user: userPayload.data,
                  data: userPayload.data,
                  userData: userPayload
                },
              });
              return;
            }
          } catch (apiErr) {
            console.error('Authentication verification error:', apiErr);
          }

          // Fallback: If authenticate API temporarily failed or returned non-200, check if token is still valid
          if (parsedUserData && verifyToken(serviceToken)) {
            dispatch({
              type: LOGIN,
              payload: {
                isLoggedIn: true,
                user: parsedUserData.data,
                data: parsedUserData.data,
                userData: parsedUserData
              },
            });
            return;
          }
        }

        // If no token or token is invalid/expired
        setSession(null);
        window.localStorage.removeItem('userData');
        dispatch({
          type: LOGOUT
        });
      } catch (err) {
        console.error('Session init error:', err);
        setSession(null);
        window.localStorage.removeItem('userData');
        dispatch({
          type: LOGOUT
        });
      }
    };

    init();
  }, []);

  const login = async (login_info, password) => {
    try {
        const responseOriginal = await axios.post(apiBaseUrl + 'login', 
          { login_info, password, login_source: 6 });
        const response = getResponseInterceptor(responseOriginal);
        const { data = {}, serviceToken = "", entitlement = {} } = response?.data?.response || {};

        if (response?.status === 200 && response?.data?.status === true) {
            setSession(serviceToken);
            const userPayload = { data, serviceToken, entitlement };
            window.localStorage.setItem('userData', JSON.stringify(userPayload));
            dispatch({
                type: LOGIN,
                payload: {
                    isLoggedIn: true,
                    user: data,
                    data: { data },
                    userData: userPayload
                },
            });
        } else {
            setSession(null);
            window.localStorage.removeItem('userData');
            dispatch({ type: LOGOUT });
            throw new Error(response?.data?.response?.message || "Login failed");
        }
    } catch (error) {
        throw new Error(error.message || "An error occurred during login");
    }
};


  // const oyaloLogin = async (employee_code, password) => {
  //   const responseOriginal = 
  //  await axios.post( oyaloUrl + 'web-login',
  //    { employee_code, password });
  //    const response = getResponseInterceptor(responseOriginal);
  //    const { data = {}, serviceToken = "", entitlement = {} } = response?.data?.response || {};
  //    if(response?.status === 200 && response?.data?.status === true) {
  //      setSession(response?.data?.data?.serviceToken);
  //      dispatch({
  //        type: LOGIN,
  //        payload: {
  //          isLoggedIn: true,
  //          data:{data},
  //          userData: {data, serviceToken, entitlement}
  //        },
  //      });
  //    } else {
  //      dispatch({
  //        type: LOGOUT
  //      });
  //      throw {message: response?.data?.response?.message};
  //    }
  // };


  const register = async (email, password, firstName, lastName) => {
    // todo: this flow need to be recode as it not verified
    const id = chance.bb_pin();
    const response = await axios.post('/api/account/register', {
      id,
      email,
      password,
      firstName,
      lastName
    });
    let users = response.data;

    if (
      window.localStorage.getItem('users') !== undefined
      && window.localStorage.getItem('users') !== null
    ) {
      const localUsers = window.localStorage.getItem('users');
      users = [
        ...JSON.parse(localUsers),
        {
          id,
          email,
          password,
          name: `${firstName} ${lastName}`
        }
      ];
    }

    window.localStorage.setItem('users', JSON.stringify(users));
  };

  const logout = async () => {
    try {
        const responseOriginal = await axios.post(apiBaseUrl + 'logout', {});
        getResponseInterceptor(responseOriginal);
    } catch (error) {
        console.error('Logout error:', error);
    } finally {
        setSession(null);
        window.localStorage.removeItem('userData');
        dispatch({ type: LOGOUT });
    }
  };

  const resetPassword = async (mobile) => {
    const responseOriginal = 
   await axios.post(apiBaseUrl + 'checkSms', { mobile });
     const response = getResponseInterceptor(responseOriginal);
     if(response?.status === 200 && response?.data?.status === true) {
      dispatch({
       type: LOGOUT
      });
    } else {
      dispatch({
        type: LOGOUT
      });
      throw {message: response?.data?.response?.message};
    }
  };

 
  const updatePassword = async () => {
  
  };

  const otpValidation = async ( ) => {
       //  dispatch({
       //    payload: {
       //      isLoggedIn: true,
       //      userData: {loginInfo },
       //    }
       //  });
   };

  const deactivate = async () => {
     const responseOriginal =  await axios.post(apiBaseUrl + 'user-deactivate-account', { });
     const response = getResponseInterceptor(responseOriginal);
     const { data = '', message = '' } = response?.data?.response || {};
        dispatch({
          type: LOGOUT,
        });
        throw {message: data + message};
   };

  const updateProfile = () => {};

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return (
  <JWTContext.Provider value={{ ...state, login, logout, register, resetPassword, 
    updateProfile, deactivate, otpValidation, updatePassword }}>
    {children}
  </JWTContext.Provider>
  );
};

JWTProvider.propTypes = {
  children: PropTypes.node
};

export default JWTContext;
